import { useEffect, useRef, useState, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useSocket } from '../../contexts/SocketContext'
import { useAuth } from '../../contexts/AuthContext'
import { useAudioAlerts } from '../../hooks/useAudioAlerts'
import { useNotifications } from '../../contexts/NotificationContext'
import { useAppMode } from '../../contexts/AppModeContext'
import { apiGet, apiPost, getErrorMessage } from '../../utils/api'
import { toast } from './NotificationToast'
import { isOnCallInDevMode } from '../../utils/onCall'

/**
 * Mounted once globally (inside <Layout>) for every authenticated agent.
 *
 *  - Subscribes app-wide to `new_chat` / `chat_assigned` / `chat_closed`.
 *  - Rings (continuous) AND shows a top-right pickup alert card for each new
 *    WIDGET chat, and keeps both going until someone Accepts (assigns) or the
 *    chat closes.
 *  - Accept  → assigns the chat to me + opens it; the resulting `chat_assigned`
 *    dismisses the alert + stops the ring for EVERY agent.
 *  - Decline → dismisses the card + stops the ring for THIS agent only; the chat
 *    stays queued and keeps ringing for the other agents.
 *  - Reconciles against the server's queue every RECONCILE_MS so out-of-band
 *    closes stop alerting and already-queued chats appear on login.
 *
 *  Widget chats only (WhatsApp keeps its own queue alerting).
 */

const RECONCILE_MS = 10_000

interface AlertData {
  id: string
  name: string
  company: string | null
  message: string
  pageUrl: string | null
  createdAt: string
  /** True for a chat TRANSFERRED to me (already assigned to me, ring until opened). */
  transfer?: boolean
  /** For a transfer: the name of the agent who transferred it. */
  byName?: string
  /** True for a customer REPLY on my assigned chat while I'm away from it
   *  (backgrounded tab / different screen) — rings until I open the chat. */
  attention?: boolean
  /** True when Jesse (the AI) handed this in-progress chat to a human — show a
   *  "from Jesse" label and hide the "Send to Jesse" action. */
  fromAi?: boolean
}

function toAlert(raw: any): AlertData | null {
  const id = String(raw?.id || raw?.chat_id || '')
  if (!id) return null
  const channel = raw?.channel || 'widget'
  if (channel === 'whatsapp') return null // widget only
  const company = raw?.company_name || raw?.companyName || null
  const name = raw?.visitor_name || raw?.customer_name || raw?.customerName || company || 'Anonymous user'
  const message = raw?.first_message || raw?.first_customer_message || raw?.firstCustomerMessage || ''
  const pageUrl = raw?.page_url || raw?.current_page || raw?.landing_page || null
  const createdAt = raw?.created_at || raw?.createdAt || new Date().toISOString()
  // Jesse handing over: the socket new_chat carries from_ai; the /queue poll
  // carries ai_state='escalated'. Either means this is a live chat from Jesse.
  const fromAi = !!raw?.from_ai || raw?.ai_state === 'escalated' || raw?.aiState === 'escalated'
  return { id, name, company, message, pageUrl, createdAt, fromAi }
}

function initials(name?: string): string {
  if (!name) return '?'
  return name.trim().split(/\s+/).slice(0, 2).map(w => w[0]?.toUpperCase() || '').join('') || '?'
}
function elapsed(fromIso: string, nowMs: number): string {
  const s = Math.max(0, Math.floor((nowMs - new Date(fromIso).getTime()) / 1000))
  const m = Math.floor(s / 60)
  return `${String(m).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`
}

export function GlobalQueueAlerts() {
  const { socket } = useSocket()
  const { mode, forceSupport } = useAppMode()
  const { currentAgent } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const { startRinging, stopRinging, setRingingChats, setServerMuted, isEffectivelyMuted, audioBlocked, enableAudio, playPickupAlert } = useAudioAlerts()
  const { requestNotificationPermission } = useNotifications()

  const [alerts, setAlerts] = useState<AlertData[]>([])
  const [tick, setTick] = useState(Date.now())
  const [busy, setBusy] = useState<Record<string, boolean>>({})

  const declinedRef = useRef<Set<string>>(new Set())
  // Chats TRANSFERRED to me and not yet opened. These are ASSIGNED (to me), not
  // queued, so reconcile() would drop them from the ring — we keep them here and
  // union them back in on every reconcile, then clear when I open/close the chat.
  const transferRingRef = useRef<Map<string, AlertData>>(new Map())
  const startRef = useRef(startRinging); startRef.current = startRinging
  const stopRef = useRef(stopRinging); stopRef.current = stopRinging
  const setRef = useRef(setRingingChats); setRef.current = setRingingChats
  const serverMuteRef = useRef(setServerMuted); serverMuteRef.current = setServerMuted
  const playPickupRef = useRef(playPickupAlert); playPickupRef.current = playPickupAlert
  // Latest mute state for use inside the socket callbacks (avoids stale closure).
  const mutedRef = useRef(isEffectivelyMuted); mutedRef.current = isEffectivelyMuted
  const navRef = useRef(navigate); navRef.current = navigate

  /**
   * B1 — foreground browser notification for an incoming chat. Honours the same
   * mute logic as the chime, only fires when permission is granted, and is
   * suppressed when the agent is already focused on this chat. Clicking it
   * focuses the tab and routes to the chat.
   */
  const notifyIncoming = useCallback((a: AlertData) => {
    try {
      if (mutedRef.current) return
      if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return
      if (document.hasFocus() && window.location.pathname.includes(`/queue/${a.id}`)) return
      const n = new Notification(`New chat from ${a.name}`, {
        body: a.message || 'Waiting to be picked up',
        tag: `wq-chat-${a.id}`,
        icon: '/favicon.ico',
      })
      n.onclick = () => { window.focus(); navRef.current(`/queue/${a.id}`); n.close() }
      console.info('[notify] new_chat notification shown', a.id)
    } catch { /* never let a notification failure break the flow */ }
  }, [])

  const removeAlert = useCallback((id: string) => {
    setAlerts(prev => prev.filter(a => a.id !== id))
  }, [])

  const upsertAlert = useCallback((data: AlertData) => {
    setAlerts(prev => {
      const i = prev.findIndex(a => a.id === data.id)
      if (i === -1) return [...prev, data]
      // Merge — keep the most informative fields (a later reconcile may enrich).
      const merged = { ...prev[i], ...data, name: data.name || prev[i].name, company: data.company || prev[i].company, message: data.message || prev[i].message, pageUrl: data.pageUrl || prev[i].pageUrl }
      const copy = [...prev]; copy[i] = merged; return copy
    })
  }, [])

  // Clear a transfer ring (opened / closed / declined): drop it from the ref,
  // the visible card, and my ring set.
  const clearTransfer = useCallback((id: string) => {
    if (transferRingRef.current.delete(id)) {
      removeAlert(id)
      stopRef.current(id)
    }
  }, [removeAlert])

  /**
   * Re-sync the alert list + ring set with the server's canonical queued chats.
   * Anything no longer queued (picked up on ANY device, closed, missed) is dropped
   * from the ring. Called on a timer AND on every `queue_update` socket event so a
   * pickup on mobile stops the ring on the PC within a network round-trip.
   */
  const reconcile = useCallback(async () => {
    try {
      const resp = await apiGet<any>('/queue', { status: 'queued' })
      const raw = Array.isArray(resp) ? resp : (resp?.queue || resp?.chats || [])
      const live: AlertData[] = []
      const ringIds: string[] = []
      for (const c of raw) {
        const assigned = c.assigned_agent_id || c.assignedAgentId || c.agent_id
        if (assigned) continue
        // AI First Responder: a chat the AI is actively handling must NOT ring or
        // show as an incoming pickup alert. It's still visible in the queue list
        // (with an "AI handling" badge); it rings here normally only once it
        // escalates (ai_state becomes 'escalated'), when a human is expected.
        if ((c.ai_state || c.aiState) === 'active') continue
        const a = toAlert(c)
        if (!a || declinedRef.current.has(a.id)) continue
        live.push(a)
        ringIds.push(a.id)
      }
      // Additively preserve any TRANSFER rings — they're assigned (to me), not
      // queued, so they aren't in `raw`. Without this the reconcile would drop
      // them ~400ms after the transfer's queue_update.
      for (const [id, a] of transferRingRef.current) {
        if (declinedRef.current.has(id)) continue
        if (!live.some(l => l.id === id)) live.push(a)
        if (!ringIds.includes(id)) ringIds.push(id)
      }
      setAlerts(live)
      setRef.current(ringIds)
    } catch (err: any) {
      const status = err?.status ?? err?.response?.status
      if (status === 401 || status === 403) { setAlerts([]); setRef.current([]) }
    }
  }, [])
  const reconcileRef = useRef(reconcile); reconcileRef.current = reconcile
  const queueDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  /* Live socket events */
  useEffect(() => {
    if (!socket || !currentAgent) return

    const onNewChat = (raw: any) => {
      const a = toAlert(raw)
      if (!a || declinedRef.current.has(a.id)) return
      // AI-handled chats never ring (the server suppresses their new_chat emit;
      // this is belt-and-braces for any that slip through).
      if (raw?.ai_state === 'active') return
      // Notice hours: a chat opened while a widget notice is active converts to a
      // ticket on the first message. It must NEVER ring or show as an actionable
      // incoming alert — UNCONDITIONALLY, regardless of the per-notice mute
      // switch. It still appears in the Missed/Tickets views (those fetch their
      // own lists); we just don't raise an incoming alert here.
      if (raw?.noticeMode === true) {
        serverMuteRef.current(true)
        return
      }
      // Always surface the chat in the visible alert list — mute SILENCES,
      // it never HIDES. The server decides muting (e.g. an active notice with
      // mute_alerts); when `muted` is true we add the card but skip ringing.
      // WhatsApp chats are already filtered out by toAlert(), so notice-mute
      // never affects them here.
      upsertAlert(a)
      const muted = raw?.muted === true
      if (muted) {
        serverMuteRef.current(true)
      } else {
        // Normal-hours chat: clear any stale server-mute left over from a prior
        // notice/muted chat (otherwise _serverMuted stays stuck on and silences
        // every subsequent normal chat), then ring + notify.
        serverMuteRef.current(false)
        startRef.current(a.id)
        notifyIncoming(a)
      }
    }
    const onAssigned = (data: any) => {
      const id = String(data?.chatId || data?.chat_id || data?.id || '')
      if (!id) return
      // Pickup notification (Lee, 14 Aug 2026): when SOMEONE ELSE picks up a
      // chat, play the distinct pickup ding + show who took it — so the team
      // hears the pickup, not just the ring stopping.
      const byId = data?.agentId ?? data?.agent_id
      const byName = data?.agentName ?? data?.agent_name
      if (byId != null && String(byId) !== String(currentAgent?.id ?? '')) {
        playPickupRef.current()
        if (byName) toast.info?.('Chat picked up', `${byName} picked up the chat`)
      }
      // A TRANSFER to me also broadcasts chat_assigned (to the queue room). Don't
      // let it silence my transfer ring — that ring must persist until I OPEN the
      // chat. clearTransfer on open/close handles removal instead.
      if (transferRingRef.current.has(id)) return
      removeAlert(id); stopRef.current(id)
    }
    // A chat TRANSFERRED to me: ring like a new chat until I open it. The chat is
    // already assigned to me, so it isn't in the queue — we track it separately
    // (transferRingRef) and union it into reconcile so it isn't dropped.
    const onTransferred = (data: any) => {
      if (!currentAgent) return
      const id = String(data?.chatId || data?.chat_id || data?.id || '')
      if (!id) return
      if (String(data?.agentId ?? data?.agent_id) !== String(currentAgent.id)) return
      if (declinedRef.current.has(id)) declinedRef.current.delete(id)
      const a: AlertData = {
        id,
        name: data?.name || 'Customer',
        company: data?.company || null,
        message: data?.message || 'Chat transferred to you',
        pageUrl: null,
        createdAt: data?.created_at || new Date().toISOString(),
        transfer: true,
        byName: data?.byName || 'An agent',
      }
      transferRingRef.current.set(id, a)
      upsertAlert(a)
      serverMuteRef.current(false)
      startRef.current(id)
      notifyIncoming(a)
    }
    // A customer replied on a chat ASSIGNED TO ME. Ring like a new chat —
    // but ONLY when I'm not actively looking at that chat (backgrounded tab,
    // unfocused window, or a different screen). Uses the same additive
    // personal-ring mechanism as transfers: survives reconcile, clears when
    // I open the chat (route watch), close/decline it, or reply from my phone.
    const onAssignedMessage = (data: any) => {
      if (!currentAgent) return
      const id = String(data?.chatId || data?.chat_id || '')
      if (!id) return
      if (String(data?.agentId ?? data?.agent_id) !== String(currentAgent.id)) return
      // Ring ONLY when the tab is genuinely NOT VISIBLE (minimised, another
      // tab, browser hidden). Do NOT use document.hasFocus(): it goes false
      // whenever the WINDOW loses focus even though the tab is fully visible
      // on screen (two browser windows side by side, DevTools focused…), which
      // made this ring while the agent was looking straight at the dashboard.
      // Visible tab = present = silent.
      if (document.visibilityState === 'visible') return
      if (declinedRef.current.has(id)) declinedRef.current.delete(id)
      const a: AlertData = {
        id,
        name: data?.name || 'Customer',
        company: data?.company || null,
        message: data?.message || 'New reply on your chat',
        pageUrl: null,
        createdAt: data?.created_at || new Date().toISOString(),
        transfer: true, // Accept = open (no assign call — it's already mine)
        attention: true,
      }
      transferRingRef.current.set(id, a)
      upsertAlert(a)
      serverMuteRef.current(false)
      startRef.current(id)
      notifyIncoming(a)
    }

    const onClosed = (payload: any) => {
      const id = String(typeof payload === 'string' ? payload : (payload?.chat_id || payload?.chatId || ''))
      if (!id) return
      transferRingRef.current.delete(id)
      removeAlert(id); stopRef.current(id)
    }
    // A chat that times out / is missed should stop ringing, same as when it
    // gets assigned or closed.
    const onMissed = (payload: any) => {
      const id = String(typeof payload === 'string' ? payload : (payload?.chatId || payload?.chat_id || ''))
      if (!id) return
      transferRingRef.current.delete(id)
      removeAlert(id); stopRef.current(id)
    }

    // Any queue change (a pickup from ANY device, close, etc.) → re-sync the ring
    // shortly after, so the ring stops even if the precise per-chat event was
    // missed or the pickup happened on mobile.
    const onQueueUpdate = () => {
      if (queueDebounceRef.current) clearTimeout(queueDebounceRef.current)
      queueDebounceRef.current = setTimeout(() => { void reconcileRef.current() }, 400)
    }

    // If I reply from ANOTHER device (phone), the agent message comes through
    // `new_message` — clear the attention ring for that chat: I've clearly
    // seen it.
    const onAnyMessage = (payload: any) => {
      const id = String(payload?.chatId || payload?.chat_id || '')
      if (!id) return
      const sender = payload?.message?.sender_type || payload?.message?.senderType
      if (sender !== 'agent') return
      const entry = transferRingRef.current.get(id)
      if (entry?.attention) clearTransfer(id)
    }

    socket.on('new_chat', onNewChat)
    socket.on('chat_assigned', onAssigned)
    socket.on('chat_transferred', onTransferred)
    socket.on('assigned_chat_message', onAssignedMessage)
    socket.on('new_message', onAnyMessage)
    socket.on('chat_closed', onClosed)
    socket.on('chat_missed', onMissed)
    socket.on('queue_update', onQueueUpdate)
    return () => {
      socket.off('new_chat', onNewChat)
      socket.off('chat_assigned', onAssigned)
      socket.off('chat_transferred', onTransferred)
      socket.off('assigned_chat_message', onAssignedMessage)
      socket.off('new_message', onAnyMessage)
      socket.off('chat_closed', onClosed)
      socket.off('chat_missed', onMissed)
      socket.off('queue_update', onQueueUpdate)
      if (queueDebounceRef.current) clearTimeout(queueDebounceRef.current)
    }
  }, [socket, currentAgent, upsertAlert, removeAlert, notifyIncoming, clearTransfer])

  /* Clear a transfer ring as soon as I OPEN that chat (any route containing its id). */
  useEffect(() => {
    if (transferRingRef.current.size === 0) return
    for (const id of [...transferRingRef.current.keys()]) {
      if (location.pathname.includes(`/${id}`)) clearTransfer(id)
    }
  }, [location.pathname, clearTransfer])

  /* ATTENTION rings exist only because the agent was away — the moment they
     return to the tab, stop those rings (the inbox shows the new message).
     TRANSFER rings persist: they need an explicit pickup/open. */
  useEffect(() => {
    const onReturn = () => {
      if (document.visibilityState !== 'visible') return
      for (const [id, a] of [...transferRingRef.current]) {
        if (a.attention) clearTransfer(id)
      }
    }
    document.addEventListener('visibilitychange', onReturn)
    window.addEventListener('focus', onReturn)
    return () => {
      document.removeEventListener('visibilitychange', onReturn)
      window.removeEventListener('focus', onReturn)
    }
  }, [clearTransfer])

  /* Reconcile with the server's canonical queue (timer backstop) */
  useEffect(() => {
    if (!currentAgent) return
    void reconcile()
    const interval = setInterval(() => { void reconcile() }, RECONCILE_MS)
    return () => clearInterval(interval)
  }, [currentAgent, reconcile])

  /* 1s tick to advance the elapsed timers (only while alerts are showing) */
  useEffect(() => {
    if (alerts.length === 0) return
    const t = setInterval(() => setTick(Date.now()), 1000)
    return () => clearInterval(t)
  }, [alerts.length])

  const accept = async (a: AlertData) => {
    if (!currentAgent) return
    // A clear user gesture — a good, non-aggressive moment to ask for browser
    // notification permission (no-op if already granted/denied).
    void requestNotificationPermission()
    // A transfer is already assigned to me — just open it (no assign call, which
    // would 409 on an already-assigned chat).
    if (a.transfer) {
      clearTransfer(a.id)
      navigate(`/queue/${a.id}`)
      return
    }
    setBusy(b => ({ ...b, [a.id]: true }))
    try {
      await apiPost(`/chats/${a.id}/assign`, { agentId: currentAgent.id })
      removeAlert(a.id); stopRef.current(a.id)
      // Picked up from Dev Mode → shift straight to the Support side (no dialog)
      if (mode === 'dev') forceSupport()
      navigate(`/queue/${a.id}`)
    } catch (err) {
      toast.error('Could not pick up chat', getErrorMessage(err, 'It may have been taken already'))
      // Likely taken by someone else — clear it locally.
      removeAlert(a.id); stopRef.current(a.id)
    } finally {
      setBusy(b => { const n = { ...b }; delete n[a.id]; return n })
    }
  }

  const decline = (a: AlertData) => {
    declinedRef.current.add(a.id)
    transferRingRef.current.delete(a.id) // if it was a transfer, stop tracking it
    removeAlert(a.id)
    stopRef.current(a.id) // stops MY ring only; other agents keep ringing
  }

  // Hand the ringing chat to Jesse (the AI) now, instead of waiting the 60s
  // auto-takeover. Jesse picks it up like an agent ("Picked up by Jesse").
  const sendJesse = async (a: AlertData) => {
    setBusy(b => ({ ...b, [a.id]: true }))
    try {
      await apiPost(`/chats/${a.id}/send-to-jesse`, {})
      removeAlert(a.id); stopRef.current(a.id) // Jesse's got it — stop my ring
    } catch (err) {
      toast.error('Could not send to Jesse', getErrorMessage(err, 'It may have been picked up already'))
      removeAlert(a.id); stopRef.current(a.id)
    } finally {
      setBusy(b => { const n = { ...b }; delete n[a.id]; return n })
    }
  }

  // Dev Mode gate: normally no alert card surfaces there. EXCEPTION (Lee,
  // 4 Sep): on-call agents (utils/onCall.ts) keep the full ring + pickup card
  // in Dev Mode — the DevMode shell mounts this component for exactly them,
  // and Accept shifts them back to the Support side. All hooks run above this
  // line, so the early return keeps hook order stable.
  if (mode === 'dev' && !isOnCallInDevMode(currentAgent)) return null

  if (alerts.length === 0 && !audioBlocked) return null

  return (
    <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-3 w-[360px] max-w-[calc(100vw-2rem)]">
      {audioBlocked && (
        <button
          onClick={() => { enableAudio(); void requestNotificationPermission() }}
          className="flex items-center gap-2 rounded-xl border border-amber-300 bg-amber-50 text-amber-900 shadow-lg px-4 py-3 text-sm font-medium hover:bg-amber-100 transition-colors text-left"
        >
          <svg className="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
          </svg>
          Click to enable sound &amp; notifications
        </button>
      )}
      {alerts.map(a => a.attention ? (
        /* TEMPORARY away-reply alert: no Accept/Decline — it exists only while
           the agent is away and clears itself the moment they return to the
           tab (or the chat is opened/answered). Clicking opens the chat. */
        <button
          key={a.id}
          onClick={() => { clearTransfer(a.id); if (mode === 'dev') forceSupport(); navigate(`/queue/${a.id}`) }}
          className="text-left rounded-xl border border-border bg-surface shadow-xl overflow-hidden animate-fade-in p-3.5 hover:bg-surface-hover transition-colors"
        >
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-brand-100 text-brand-700 flex items-center justify-center text-xs font-bold shrink-0">
              {initials(a.name)}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-txt-primary truncate">{a.name}</p>
              <p className="text-sm text-txt-primary mt-0.5 line-clamp-2">{a.message}</p>
              <p className="text-[11px] text-txt-tertiary mt-1">New reply on your open chat — clears when you're back</p>
            </div>
          </div>
        </button>
      ) : (
        <div key={a.id} className="rounded-xl border border-border bg-surface shadow-xl overflow-hidden animate-fade-in">
          <div className="p-4">
            {/* Header */}
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-full bg-brand-100 text-brand-700 flex items-center justify-center text-xs font-bold shrink-0">
                {initials(a.name)}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-txt-primary truncate">{a.name}</p>
                {a.company && <p className="text-xs text-txt-secondary truncate">{a.company}</p>}
                {a.fromAi && (
                  <span className="inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                    Handed over by Jesse — needs attention
                  </span>
                )}
              </div>
              <svg className="w-5 h-5 text-brand-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.7}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
              </svg>
            </div>

            {/* Message */}
            <p className="text-sm text-txt-primary mt-2.5 line-clamp-2">{a.message || (a.transfer ? 'Chat transferred to you' : 'New chat — waiting to be picked up')}</p>

            {/* Notes */}
            <p className="text-xs text-txt-tertiary mt-1.5">
              {a.attention
                ? 'New reply on your open chat — tap Accept to open it'
                : a.fromAi
                ? "Jesse couldn't resolve this — an open chat that needs a human"
                : a.transfer
                ? `Transferred to you by ${a.byName || 'an agent'}`
                : 'Notes: New chat via the WeQuote widget'}
            </p>

            {/* Department + page URL */}
            <div className="flex items-center gap-2 mt-2.5 text-xs">
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-surface-secondary text-txt-secondary">
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9.348 14.652a3.75 3.75 0 010-5.304m5.304 0a3.75 3.75 0 010 5.304m-7.425 2.121a6.75 6.75 0 010-9.546m9.546 0a6.75 6.75 0 010 9.546M5.106 18.894c-3.808-3.808-3.808-9.98 0-13.788m13.788 0c3.808 3.808 3.808 9.98 0 13.788M12 12h.008v.008H12V12z" /></svg>
                Support
              </span>
              {a.pageUrl && (
                <span className="inline-flex items-center gap-1 text-brand-600 min-w-0">
                  <svg className="w-3 h-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9 9 0 100-18 9 9 0 000 18zm0 0a8.949 8.949 0 004.951-1.488A3.987 3.987 0 0013 16h-2a3.987 3.987 0 00-3.951 3.512A8.949 8.949 0 0012 21zm0-18v6m-9 3h18" /></svg>
                  <span className="truncate">{a.pageUrl.replace(/^https?:\/\//, '')}</span>
                </span>
              )}
            </div>

            {/* Timer + actions */}
            <div className="flex items-center justify-between mt-3.5">
              <span className="inline-flex items-center gap-1 text-xs text-txt-secondary tabular-nums">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                {elapsed(a.createdAt, tick)}
              </span>
              <div className="flex items-center gap-2">
                <button onClick={() => decline(a)} className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-border text-txt-secondary hover:bg-surface-hover transition-colors">Decline</button>
                {!a.transfer && !a.fromAi && (
                  <button onClick={() => sendJesse(a)} disabled={!!busy[a.id]} className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-brand-300 text-brand-700 dark:text-brand-300 hover:bg-brand-50 dark:hover:bg-brand-900/20 disabled:opacity-50 transition-colors" title="Let Jesse (AI) handle this chat">Send to Jesse</button>
                )}
                <button onClick={() => accept(a)} disabled={!!busy[a.id]} className="px-3.5 py-1.5 rounded-lg text-xs font-semibold bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50 transition-colors">{busy[a.id] ? 'Accepting…' : 'Accept'}</button>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}
